import React, { useState, useEffect, useMemo } from 'react';
import { fetchDataFromApi } from '../../api/googleSheet';
import { parseDate, renderDate } from '../../utils/dateUtils';
import { IconSearch, IconChevronUp, IconChevronDown } from '../common/Icons';

const getStatusClass = (status) => {
    switch (status) {
        case "เปิดรับสมัคร": return "bg-green-100 text-green-800";
        case "เต็มแล้ว":
        case "จบแล้ว": return "bg-red-100 text-red-800";
        case "เร็วๆ นี้": return "bg-yellow-100 text-yellow-800";
        default: return "bg-gray-100 text-gray-800";
    }
};

const RunningTable = ({ selectedYear }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "Date", direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (!selectedYear) return;

        const loadData = async () => {
            setLoading(true);
            setError(null);
            setCurrentPage(1);
            const sheetName = `Event${selectedYear + 543}`;
            try {
                const events = await fetchDataFromApi(["ID", "Event", "Date", "Location", "Prize", "Status"], sheetName);
                setData(events);
            } catch (err) {
                console.error(err);
                setError(`ไม่สามารถโหลดข้อมูลของปี ${selectedYear} ได้`);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [selectedYear]);

    const processedData = useMemo(() => {
        let filteredData = data.filter(item =>
            item && typeof item === 'object' && Object.values(item).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        if (sortConfig.key) {
            filteredData.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];
                if (sortConfig.key === 'Date') {
                    const dateA = parseDate(valA);
                    const dateB = parseDate(valB);
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    valA = dateA.getTime();
                    valB = dateB.getTime();
                }
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return filteredData;
    }, [data, searchTerm, sortConfig]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return processedData.slice(startIndex, startIndex + rowsPerPage);
    }, [processedData, currentPage, rowsPerPage]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(processedData.length / rowsPerPage);

    const TableHeader = ({ columnKey, title, className = '' }) => (
        <th scope="col" className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none ${className}`} onClick={() => handleSort(columnKey)}>
            {title}
            {sortConfig.key === columnKey && (sortConfig.direction === 'asc' ? <IconChevronUp /> : <IconChevronDown />)}
        </th>
    );
    
    if (loading) return <div className="flex justify-center items-center h-96"><div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div></div>;
    if (error) return <div className="p-8 bg-red-50 rounded-lg text-center"><h2 className="text-2xl font-bold text-red-600">เกิดข้อผิดพลาด</h2><p className="mt-2 text-red-600">{error}</p></div>;

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="relative">
                    <IconSearch />
                    <input type="text" placeholder="ค้นหางานวิ่ง, สถานที่, หรือสถานะ..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <TableHeader columnKey="ID" title="#" className="w-12"/>
                            <TableHeader columnKey="Event" title="Event" />
                            <TableHeader columnKey="Date" title="Date" />
                            <TableHeader columnKey="Location" title="Location" />
                            <TableHeader columnKey="Prize" title="Prize" />
                            <TableHeader columnKey="Status" title="Status" />
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedData.length > 0 ? paginatedData.map((item, index) => (
                            <tr key={`event-row-${item.ID}-${index}`} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.ID}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-800 font-semibold">{item.Event}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{renderDate(item.Date, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.Location}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.Prize}</td>
                                <td className="px-4 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(item.Status)}`}>{item.Status}</span></td>
                            </tr>
                        )) : (
                            <tr><td colSpan="6" className="text-center py-12 text-gray-500">ไม่พบข้อมูลที่ตรงกัน</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="p-4 sm:p-6 bg-white border-t border-gray-200 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">หน้า {currentPage} จาก {totalPages}</span>
                    <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border-gray-300 rounded-md text-sm">
                        {[10, 20, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                    </select>
                </div>
                <nav className="flex gap-1">
                    <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">ก่อนหน้า</button>
                    <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">ถัดไป</button>
                </nav>
            </div>
        </div>
    );
};

export default RunningTable;
